import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, MapPin, Phone, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", address: "", phone: "", email: "", status: "active" };

export default function Branches() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/branches").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, address: b.address || "", phone: b.phone || "", email: b.email || "", status: b.status }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/branches/${editing.id}`, form); toast.success("Şube güncellendi"); }
      else { await api.post("/branches", form); toast.success("Şube oluşturuldu"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-branches-page">
      <PageHeader title="Şubeler" subtitle="Çoklu şube & franchise için hazır yapı"
        action={<Button onClick={openNew} data-testid="add-new-branch-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Şube</Button>} />

      {items === null ? (
        <div className="grid sm:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-[#13131C]" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b) => (
            <div key={b.id} data-testid={`branch-card-${b.id}`} className="rounded-2xl bg-[#13131C] border border-[#222230] p-5 hover:border-[#FF5500]/40 transition-all group">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#FF5500]/10 flex items-center justify-center"><MapPin size={20} className="text-[#FF5500]" /></div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={b.status} />
                  <button onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536] opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={15} /></button>
                </div>
              </div>
              <h3 className="font-display font-bold text-white text-lg mt-3">{b.name}</h3>
              <div className="space-y-1.5 mt-3 text-sm text-[#A0A0B2]">
                {b.address && <p className="flex items-center gap-2"><MapPin size={14} className="text-[#68687B]" /> {b.address}</p>}
                {b.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-[#68687B]" /> {b.phone}</p>}
                {b.email && <p className="flex items-center gap-2"><Mail size={14} className="text-[#68687B]" /> {b.email}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Şube Düzenle" : "Yeni Şube"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Şube Adı</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Adres</label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Telefon</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">E-posta</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="branch-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
